import TeceoLogo from '@components/v2/ui/TeceoLogo/TeceoLogo';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Box,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  useTheme,
} from '@mui/material';
import * as React from 'react';
import ButtonWithFeddback, { ButtonStatusVariant } from '../../../components/ButtonWithFeddback';
import Typography from '../../../components/Typography/Typography';
import LangEnum from '../../../lang/LangEnum';
import { authBackgroundStyle } from '../../../styles/authBackground.styles';
import { useTranslation } from '../../translation/TranslationContext';

interface OAuthConsentFormProps {
  clientName: string;
  scopes: string[];
  onConsent: () => Promise<void>;
  onDeny: () => void;
  status: ButtonStatusVariant;
}

const SCOPE_LABEL_MAP: Record<string, LangEnum> = {
  email: LangEnum.OAUTH_SCOPE_EMAIL,
  profile: LangEnum.OAUTH_SCOPE_PROFILE,
  mcp: LangEnum.OAUTH_SCOPE_MCP,
  api: LangEnum.OAUTH_SCOPE_API,
};

const OAuthConsentForm: React.FC<OAuthConsentFormProps> = ({
  clientName,
  scopes,
  onConsent,
  onDeny,
  status,
}) => {
  const { translate } = useTranslation();
  const theme = useTheme();
  const isSubmitDisabled = status === 'loading' || status === 'error';

  return (
    <div style={{ ...authBackgroundStyle(theme) }}>
      <Paper sx={{ padding: 3 }} elevation={0}>
        <Container maxWidth="xs" disableGutters>
          <Box display="flex" justifyContent="center" mb={3}>
            <TeceoLogo />
          </Box>
          <Typography variant="h8-medium" align="center" gutterBottom>
            {translate(LangEnum.OAUTH_CONSENT_TITLE, { clientName })}
          </Typography>
          {scopes?.length > 0 && (
            <List>
              {scopes.map((scope) => (
                <ListItem key={scope} sx={{ paddingLeft: 0, paddingRight: 0 }}>
                  <ListItemIcon>
                    <CheckCircleOutlineIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={SCOPE_LABEL_MAP[scope] ? translate(SCOPE_LABEL_MAP[scope]) : scope}
                  />
                </ListItem>
              ))}
            </List>
          )}
          <Stack direction="row" gap={2} marginTop={2}>
            <ButtonWithFeddback
              variant="outlined"
              size="large"
              color="inherit"
              fullWidth
              disabled={isSubmitDisabled}
              onClick={onDeny}
            >
              {translate(LangEnum.OAUTH_CONSENT_DENY)}
            </ButtonWithFeddback>
            <ButtonWithFeddback
              variant="contained"
              size="large"
              color="primary"
              fullWidth
              status={status}
              disabled={isSubmitDisabled}
              onClick={onConsent}
            >
              {translate(LangEnum.OAUTH_CONSENT_ALLOW)}
            </ButtonWithFeddback>
          </Stack>
        </Container>
      </Paper>
    </div>
  );
};

export default OAuthConsentForm;
